import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { C, alpha } from '../lib/theme.js';
import { Button, Card, EmptyState, Eyebrow, ErrorLine } from '../ui/ui.js';
import { useToast } from '../ui/Toast.js';
import { Icon } from '../ui/icons.js';
import { apiFetch } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';
import { useOrg } from '../app/OrgContext.js';
import { driveConfigured, pickImageFromDrive, pickDriveFileRef } from '../lib/googleDrive.js';
import type { Connections, CopyGenerateResult, CopyVariant, MediaItem } from '../lib/types.js';

/* Live Copy Engine — the on-constitution creative brain.
   Per CREATIVE_PARADIGM.md, Phuket Cleanse is locked to Inspirational + Luxury/Editorial. */

interface Door {
  key: 'magic' | 'energy' | 'calm' | 'freedom';
  label: string;
  glyph: string;
  desc: string;
  color: string;
}
const DOORS: Door[] = [
  { key: 'magic', label: 'Magic', glyph: '✦', desc: 'Wonder, transformation', color: C.magic },
  { key: 'energy', label: 'Energy', glyph: '✸', desc: 'Vitality, aliveness', color: C.energy },
  { key: 'calm', label: 'Calm', glyph: '❍', desc: 'Stillness, ease', color: C.calm },
  { key: 'freedom', label: 'Freedom', glyph: '○', desc: 'Space, release', color: C.freedom },
];

// The generation "rotation" axis is per-tenant: Phuket Cleanse rotates desire doors
// (above), Propty rotates buyer segments. The picker renders whichever the tenant's
// /copy/config returns. A PickOption is the common shape (segments have no glyph/color).
interface PickOption {
  key: string;
  label: string;
  glyph: string;
  desc: string;
  color: string;
}
interface AxisMeta {
  name: string; // 'desire' | 'segment'
  label: string; // 'Desire door' | 'Buyer segment'
  values: { key: string; label: string; hint: string | null }[];
}

type Status = 'idle' | 'loading' | 'done';

// Persist the whole Copy Engine session (inputs + generated variants + edits +
// which were saved) in localStorage per org, so leaving the tab — or reloading —
// doesn't throw away generated copy. Restored on return.
interface CopySession {
  doorKey: Door['key'];
  segKey?: string;
  substantiation?: string;
  mode: 'Image-first' | 'Message-first';
  context: string;
  offer: string;
  offerCode: string;
  result: CopyGenerateResult | null;
  edits: Record<number, CopyVariant>;
  saved: number[];
  savedIds: Record<number, string>;
}
const copyKey = (orgId: string) => `basil.copySession.${orgId}`;
function readCopySession(orgId: string): CopySession | null {
  try {
    const raw = localStorage.getItem(copyKey(orgId));
    return raw ? (JSON.parse(raw) as CopySession) : null;
  } catch {
    return null;
  }
}
function writeCopySession(orgId: string, s: CopySession): void {
  try {
    localStorage.setItem(copyKey(orgId), JSON.stringify(s));
  } catch {
    // ignore quota / private-mode failures — best-effort
  }
}

export function CopyEngine() {
  const { orgId } = useOrg();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [door, setDoor] = useState<Door>(DOORS[0] as Door);
  const [mode, setMode] = useState<'Image-first' | 'Message-first'>('Message-first');
  const [context, setContext] = useState('');
  const [offer, setOffer] = useState('');
  const [offerCode, setOfferCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<CopyGenerateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Set<number>>(new Set());
  const [savedIds, setSavedIds] = useState<Record<number, string>>({});
  const [keyStatus, setKeyStatus] = useState<string | null>(null);
  const [driveFolderId, setDriveFolderId] = useState<string | null>(null);
  const [media, setMedia] = useState<MediaItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [library, setLibrary] = useState<MediaItem[]>([]);
  const [desireCounts, setDesireCounts] = useState<Record<string, number>>({});
  const [edits, setEdits] = useState<Record<number, CopyVariant>>({});
  // Per-tenant axis + compliance (from /copy/config). Defaults to Phuket Cleanse.
  const [axisMeta, setAxisMeta] = useState<AxisMeta | null>(null);
  const [segKey, setSegKey] = useState('');
  const [requireSub, setRequireSub] = useState(false);
  const [substantiation, setSubstantiation] = useState('');

  const setField = (i: number, field: keyof CopyVariant, val: string) =>
    setEdits((e) => {
      const base = e[i] ?? (result?.variants[i] as CopyVariant);
      return {
        ...e,
        [i]: {
          ...base,
          [field]: val,
          char_count: field === 'primary_text' ? val.length : base.char_count,
        },
      };
    });

  // Restore a saved session per org. This re-runs on workspace switch: the ref tracks
  // WHICH org we've hydrated for, not just whether we have. Without that, switching
  // tenants kept the previous tenant's inputs on screen AND — via the save effect
  // below — wrote them into the new tenant's localStorage, bleeding one client's copy
  // into another's. On every org change we reset to defaults, drop the previous
  // tenant's axis/segment/generated copy, then apply this org's saved session if any.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!orgId || hydratedFor.current === orgId) return;
    hydratedFor.current = orgId;
    // Clear anything tenant-specific before applying the new org's session.
    setAxisMeta(null);
    setSegKey('');
    setSubstantiation('');
    setResult(null);
    setStatus('idle');
    setEdits({});
    setSaved(new Set());
    setSavedIds({});
    setMedia(null);
    setDoor(DOORS[0] as Door);
    setMode('Message-first');
    setContext('');
    setOffer('');
    setOfferCode('');
    const s = readCopySession(orgId);
    if (!s) return;
    const d = DOORS.find((x) => x.key === s.doorKey);
    if (d) setDoor(d);
    if (s.segKey) setSegKey(s.segKey);
    setSubstantiation(s.substantiation ?? '');
    setMode(s.mode);
    setContext(s.context);
    setOffer(s.offer);
    setOfferCode(s.offerCode);
    if (s.result) {
      setResult(s.result);
      setStatus('done');
    }
    setEdits(s.edits ?? {});
    setSaved(new Set(s.saved ?? []));
    setSavedIds(s.savedIds ?? {});
  }, [orgId]);
  useEffect(() => {
    // Only persist once we've hydrated FOR THIS org, else the first render after a
    // switch would save the previous tenant's state under the new org's key.
    if (hydratedFor.current !== orgId || !orgId) return;
    writeCopySession(orgId, {
      doorKey: door.key,
      segKey,
      substantiation,
      mode,
      context,
      offer,
      offerCode,
      result,
      edits,
      saved: [...saved],
      savedIds,
    });
  }, [
    orgId,
    door,
    segKey,
    substantiation,
    mode,
    context,
    offer,
    offerCode,
    result,
    edits,
    saved,
    savedIds,
  ]);

  const loadConn = useCallback(() => {
    if (!orgId) return;
    apiFetch<Connections>(`/api/orgs/${orgId}/connections`)
      .then((d) => {
        setKeyStatus(d.anthropic.status);
        setDriveFolderId(d.drive?.folder_id ?? null);
      })
      .catch(() => setKeyStatus(null));
    apiFetch<{ media: MediaItem[] }>(`/api/orgs/${orgId}/media`)
      .then((d) => setLibrary(d.media.filter((m) => m.url)))
      .catch(() => {});
    // Per-tenant Copy Engine config: rotation axis + substantiation requirement.
    apiFetch<{ axis: AxisMeta; require_substantiation: boolean }>(`/api/orgs/${orgId}/copy/config`)
      .then((d) => {
        setAxisMeta(d.axis);
        setRequireSub(d.require_substantiation);
        if (d.axis.name !== 'desire') setSegKey((k) => k || d.axis.values[0]?.key || '');
      })
      .catch(() => {});
    // Recent desire mix, for the rotation nudge (CREATIVE_PARADIGM §3.4).
    apiFetch<{ creatives: { desire: string }[] }>(`/api/orgs/${orgId}/creatives`)
      .then((d) => {
        const counts: Record<string, number> = {};
        d.creatives.slice(0, 12).forEach((c) => {
          counts[c.desire] = (counts[c.desire] ?? 0) + 1;
        });
        setDesireCounts(counts);
      })
      .catch(() => {});
  }, [orgId]);
  useEffect(() => {
    loadConn();
  }, [loadConn]);

  // Handoff from Create ("Write copy for it →"): preselect that image, image-first.
  const preMedia = params.get('media');
  useEffect(() => {
    if (!preMedia || media) return;
    const m = library.find((x) => x.id === preMedia);
    if (m) {
      setMedia(m);
      setMode('Image-first');
    }
  }, [preMedia, library, media]);

  const connected = keyStatus === 'connected';

  // Resolve the active rotation axis. Until this tenant's /copy/config has loaded,
  // axisMeta is null and we render NOTHING — never a default. Defaulting to desire
  // doors flashed Phuket Cleanse's paradigm on Propty's screen for a beat, which for
  // a white-label multi-tenant tool is a real leak, not a cosmetic one. Once loaded:
  // Phuket Cleanse → desire doors with colors/glyphs; Propty → plain segment cards.
  const configLoaded = axisMeta !== null;
  const isDesire = axisMeta?.name === 'desire';
  const segOptions: PickOption[] = (axisMeta?.values ?? []).map((v) => ({
    key: v.key,
    label: v.label,
    glyph: '',
    desc: v.hint ?? '',
    color: C.primary,
  }));
  const pickerOptions: PickOption[] = !configLoaded ? [] : isDesire ? DOORS : segOptions;
  const selKey = isDesire ? door.key : segKey;
  const sel = pickerOptions.find((o) => o.key === selKey);
  const selColor = sel?.color ?? C.primary;
  const selGlyph = sel?.glyph ?? '';
  const axisLabel = !configLoaded ? '…' : isDesire ? 'desire' : axisMeta.label.toLowerCase();

  // Rotation nudge: bring the least-told door forward (magic gets priority). Desire-only.
  const rotationNudge = (() => {
    if (!isDesire) return null;
    const total = Object.values(desireCounts).reduce((a, b) => a + b, 0);
    if (total < 3) return null;
    const top = Object.entries(desireCounts).sort((a, b) => b[1] - a[1])[0];
    if (!top || top[1] < 2) return null;
    const order: Array<Door['key']> = ['magic', 'freedom', 'calm', 'energy'];
    const least = order.find((k) => (desireCounts[k] ?? 0) === 0) ?? order[0];
    if (top[0] === least) return null;
    return `You've leaned on ${top[0]} lately. Bring ${least} forward — the deeper, less-told door.`;
  })();

  async function uploadImage(file: File) {
    if (!orgId) return;
    setUploading(true);
    setError(null);
    try {
      const { path, token } = await apiFetch<{ path: string; token: string; mime: string }>(
        `/api/orgs/${orgId}/media/upload-url`,
        { method: 'POST', body: JSON.stringify({ mime: file.type }) },
      );
      const up = await supabase.storage.from('media').uploadToSignedUrl(path, token, file);
      if (up.error) throw up.error;
      const rec = await apiFetch<MediaItem>(`/api/orgs/${orgId}/media`, {
        method: 'POST',
        body: JSON.stringify({ path, mime: file.type, desire: selKey }),
      });
      setMedia(rec);
      setLibrary((l) => [rec, ...l.filter((x) => x.id !== rec.id)]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function fromDrive() {
    if (!orgId) return;
    setError(null);
    try {
      const pick = await pickImageFromDrive(driveFolderId, orgId);
      if (pick) await uploadImage(pick.file);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Google Drive import failed');
    }
  }

  // Attach a reel that STAYS in Google Drive — catalog a reference (no bytes), so
  // it's streamed straight to Meta at launch (nothing lands in Supabase). Basil
  // can't watch a video, so copy comes from the notes you write below.
  async function fromDriveReel() {
    if (!orgId) return;
    setError(null);
    try {
      const ref = await pickDriveFileRef(driveFolderId, orgId);
      if (!ref) return; // cancelled the picker — not an error, say nothing
      setUploading(true);
      const rec = await apiFetch<MediaItem>(`/api/orgs/${orgId}/media/link-drive`, {
        method: 'POST',
        body: JSON.stringify({ drive_file_id: ref.id, name: ref.name, mime: ref.mimeType }),
      });
      setMedia(rec);
      toast('success', `Reel attached — “${ref.name}”. It stays in Drive; nothing was uploaded.`);
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Could not link that reel from Drive';
      setError(m);
      toast('error', `Couldn't attach that reel — ${m}`);
    } finally {
      setUploading(false);
    }
  }

  async function generate() {
    if (!orgId) return;
    setStatus('loading');
    setError(null);
    setResult(null);
    setSaved(new Set());
    setEdits({});
    try {
      const r = await apiFetch<CopyGenerateResult>(`/api/orgs/${orgId}/copy/generate`, {
        method: 'POST',
        body: JSON.stringify({
          axis: selKey,
          mode: mode === 'Image-first' ? 'image_first' : 'message_first',
          context,
          offer: offer || null,
          offer_code: offerCode || null,
          // Basil can't watch a video — never send a reel to the reader; its copy
          // comes from the written notes. Images are read as before.
          media_id: mode === 'Image-first' && media?.kind !== 'video' ? (media?.id ?? null) : null,
        }),
      });
      setResult(r);
      setStatus('done');
      toast('success', `Wrote ${r.variants?.length ?? 0} options — pick one and Save post.`);
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Generation failed';
      setError(m);
      toast('error', `Couldn't write the copy — ${m}`);
      setStatus('idle');
    }
  }

  async function save(orig: CopyVariant, i: number) {
    if (!orgId) return;
    const v = edits[i] ?? orig;
    try {
      const res = await apiFetch<{ id: string }>(`/api/orgs/${orgId}/creatives`, {
        method: 'POST',
        body: JSON.stringify({
          generation_id: result?.generation_id ?? null,
          segment: selKey,
          hook_pattern: v.hook_pattern,
          primary_text: v.primary_text,
          headline: v.headline,
          description: v.description,
          blog_angle: v.blog_angle,
          shot_brief: v.shot_brief,
          substantiation: substantiation || null,
          offer_code: offerCode || null,
          // Attach a reel whatever the mode; attach an image only in image-first.
          media_id: media && (media.kind === 'video' || mode === 'Image-first') ? media.id : null,
        }),
      });
      setSaved((s) => new Set(s).add(i));
      if (res?.id) setSavedIds((m) => ({ ...m, [i]: res.id }));
      toast('success', 'Post saved. Send it to Launch when you’re ready.');
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Save failed';
      setError(m);
      toast('error', `Save failed — ${m}`);
    }
  }

  const variants = result?.variants ?? [];

  return (
    <div style={{ padding: '26px 30px 40px', maxWidth: 1120, animation: 'basilfade .3s ease' }}>
      <div style={{ marginBottom: 22 }}>
        <Eyebrow style={{ marginBottom: 9 }}>
          Signature · AI copy generation &nbsp;·&nbsp;{' '}
          <span style={{ color: connected ? C.good : C.watch }}>
            {connected ? 'LIVE' : 'KEY NOT CONNECTED'}
          </span>
        </Eyebrow>
        <h1 className="page-title">Compose</h1>
      </div>

      {!connected && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            borderRadius: 10,
            background: alpha(C.watch, 10),
            border: `1px solid ${alpha(C.watch, 30)}`,
            fontSize: 13,
            color: C.secondary,
          }}
        >
          No Anthropic key connected for this workspace. Add one in{' '}
          <strong>Connections → Copy Engine</strong> to generate on-brand copy.
        </div>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        {/* config */}
        <Card style={{ padding: 22 }}>
          <div className="label-mono" style={{ marginBottom: 13 }}>
            1 · Choose a {axisLabel}
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}
          >
            {!configLoaded && (
              <div style={{ gridColumn: '1 / -1', fontSize: 13, color: C.muted, padding: '8px 0' }}>
                Loading this workspace&rsquo;s options…
              </div>
            )}
            {pickerOptions.map((d) => {
              const active = d.key === selKey;
              return (
                <div
                  key={d.key}
                  onClick={() => {
                    if (isDesire) {
                      const dd = DOORS.find((x) => x.key === d.key);
                      if (dd) setDoor(dd);
                    } else {
                      setSegKey(d.key);
                    }
                    setStatus('idle');
                    setResult(null);
                  }}
                  style={{
                    padding: '14px 15px',
                    borderRadius: 11,
                    cursor: 'pointer',
                    background: active ? alpha(d.color, 9) : C.surface2,
                    border: `1px solid ${active ? d.color : C.line}`,
                  }}
                >
                  <div style={{ fontSize: 18, color: d.color, marginBottom: 8 }}>{d.glyph}</div>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: active ? C.primary : C.secondary,
                    }}
                  >
                    {d.label}
                  </div>
                  <div
                    style={{
                      font: `500 10px 'IBM Plex Mono',monospace`,
                      color: C.faint,
                      marginTop: 3,
                    }}
                  >
                    {d.desc}
                  </div>
                </div>
              );
            })}
          </div>

          {rotationNudge && (
            <div
              style={{
                marginBottom: 22,
                padding: '9px 12px',
                borderRadius: 9,
                background: alpha(C.magic, 9),
                border: `1px solid ${alpha(C.magic, 22)}`,
                fontSize: 11.5,
                lineHeight: 1.5,
                color: C.secondary,
              }}
            >
              ✦ {rotationNudge}
            </div>
          )}

          {requireSub && (
            <div style={{ marginBottom: 22 }}>
              <div className="label-mono" style={{ marginBottom: 8 }}>
                Substantiation{' '}
                <span style={{ color: C.faint }}>· source for any stat or testimonial</span>
              </div>
              <textarea
                value={substantiation}
                onChange={(e) => setSubstantiation(e.target.value)}
                placeholder="Link or note evidencing any claim. Required before a social-proof claim can go live (ASA/CAP)."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  minHeight: 54,
                  background: C.surface2,
                  border: `1px solid ${C.line}`,
                  borderRadius: 9,
                  padding: '8px 10px',
                  color: C.primary,
                  font: `400 12.5px 'Inter',system-ui,sans-serif`,
                }}
              />
            </div>
          )}

          <div className="label-mono" style={{ marginBottom: 11 }}>
            2 · Entry mode
          </div>
          <div
            style={{
              display: 'flex',
              gap: 3,
              background: C.surface2,
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              padding: 3,
              marginBottom: 24,
            }}
          >
            {(['Image-first', 'Message-first'] as const).map((m) => (
              <div
                key={m}
                onClick={() => setMode(m)}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '8px 0',
                  borderRadius: 7,
                  fontSize: 12,
                  cursor: 'pointer',
                  fontWeight: mode === m ? 600 : 400,
                  background: mode === m ? C.elevated : 'transparent',
                  color: mode === m ? C.primary : C.muted,
                }}
              >
                {m}
              </div>
            ))}
          </div>

          {mode === 'Image-first' && (
            <>
              <div className="label-mono" style={{ marginBottom: 11 }}>
                3 · Image <span style={{ color: C.faint }}>· Basil reads it directly</span>
              </div>
              {media?.url ? (
                <div style={{ position: 'relative', marginBottom: 24 }}>
                  <img
                    src={media.url}
                    alt="upload"
                    style={{
                      width: '100%',
                      borderRadius: 10,
                      display: 'block',
                      border: `1px solid ${C.line}`,
                      maxHeight: 220,
                      objectFit: 'cover',
                    }}
                  />
                  <span
                    onClick={() => setMedia(null)}
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: 'rgba(0,0,0,.6)',
                      color: '#fff',
                      borderRadius: 7,
                      padding: '3px 9px',
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Change
                  </span>
                </div>
              ) : media ? (
                <div
                  style={{
                    position: 'relative',
                    marginBottom: 24,
                    padding: '16px 16px 18px',
                    borderRadius: 10,
                    border: `1px solid ${C.line}`,
                    background: C.surface2,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span style={{ fontSize: 20, color: C.basil }}>▶</span>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {media.title || 'Reel from Google Drive'}
                      </div>
                      <div
                        style={{
                          font: `500 10px 'IBM Plex Mono',monospace`,
                          color: C.faint,
                          marginTop: 3,
                        }}
                      >
                        REEL · STREAMED FROM DRIVE AT LAUNCH · NOT STORED
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                    Basil can&apos;t watch a reel — write the notes below and it writes the copy
                    from those.
                  </div>
                  <span
                    onClick={() => setMedia(null)}
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 12,
                      font: `600 11px 'IBM Plex Mono',monospace`,
                      color: C.basil,
                      cursor: 'pointer',
                    }}
                  >
                    Change
                  </span>
                </div>
              ) : (
                <label
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!uploading) setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f && f.type.startsWith('image/') && !uploading) void uploadImage(f);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    marginBottom: 24,
                    padding: '30px 15px',
                    borderRadius: 10,
                    border: `1.5px dashed ${dragOver ? C.basil : C.line}`,
                    background: dragOver ? alpha(C.basil, 8) : C.surface2,
                    cursor: uploading ? 'default' : 'pointer',
                    color: dragOver ? C.basil : C.muted,
                    fontSize: 12.5,
                    textAlign: 'center',
                    transition: 'border-color .15s, background .15s, color .15s',
                  }}
                >
                  <span style={{ fontSize: 20, color: C.basil }}>↑</span>
                  {uploading
                    ? 'Uploading…'
                    : dragOver
                      ? 'Drop to upload'
                      : 'Drag an image here, or click to browse'}
                  <span style={{ fontSize: 10.5, color: C.faint }}>JPEG, PNG, WebP</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: 'none' }}
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadImage(f);
                    }}
                  />
                </label>
              )}
              {!media && driveConfigured && (
                <button
                  type="button"
                  onClick={() => void fromDrive()}
                  disabled={uploading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    width: '100%',
                    marginTop: -12,
                    marginBottom: 24,
                    padding: '9px 14px',
                    borderRadius: 9,
                    border: `1px solid ${C.line}`,
                    background: C.surface,
                    color: C.primary,
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    cursor: uploading ? 'default' : 'pointer',
                  }}
                >
                  <svg width="15" height="13" viewBox="0 0 87.3 78" aria-hidden="true">
                    <path
                      fill="#0066da"
                      d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z"
                    />
                    <path
                      fill="#00ac47"
                      d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 48.4C.4 49.8 0 51.35 0 52.9h27.5z"
                    />
                    <path
                      fill="#ea4335"
                      d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57.4c.8-1.4 1.2-2.95 1.2-4.5H59.798l5.852 11.5z"
                    />
                    <path
                      fill="#00832d"
                      d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z"
                    />
                    <path
                      fill="#2684fc"
                      d="M59.8 52.9H27.5L13.75 76.7c1.35.8 2.9 1.3 4.5 1.3h50.8c1.6 0 3.15-.5 4.5-1.3z"
                    />
                    <path
                      fill="#ffba00"
                      d="M73.4 26.5L60.75 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 27.9h27.45c0-1.55-.4-3.1-1.2-4.5z"
                    />
                  </svg>
                  Import from Google Drive
                </button>
              )}
              {!media && driveConfigured && (
                <button
                  type="button"
                  onClick={() => void fromDriveReel()}
                  disabled={uploading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    width: '100%',
                    marginTop: -12,
                    marginBottom: 24,
                    padding: '9px 14px',
                    borderRadius: 9,
                    border: `1px solid ${C.line}`,
                    background: C.surface,
                    color: C.primary,
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    cursor: uploading ? 'default' : 'pointer',
                  }}
                >
                  {uploading ? (
                    <Spinner size={13} />
                  ) : (
                    <span style={{ fontSize: 13, color: C.basil }}>▶</span>
                  )}
                  {uploading ? 'Attaching…' : 'Use a reel from Drive'}{' '}
                  {!uploading && (
                    <span style={{ color: C.faint, fontSize: 11 }}>· streamed, no upload</span>
                  )}
                </button>
              )}
              {!media && library.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      font: `500 10px 'IBM Plex Mono',monospace`,
                      color: C.faint,
                      marginBottom: 8,
                    }}
                  >
                    OR REUSE FROM YOUR LIBRARY
                  </div>
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                    {library.map((m) => (
                      <img
                        key={m.id}
                        src={m.url ?? ''}
                        alt={m.alt ?? ''}
                        onClick={() => setMedia(m)}
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: 'cover',
                          borderRadius: 8,
                          flexShrink: 0,
                          cursor: 'pointer',
                          border: `1px solid ${C.line}`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="label-mono" style={{ marginBottom: 11 }}>
            {mode === 'Image-first' ? '4 · Notes' : '3 · Context'}{' '}
            {mode === 'Image-first' && (
              <span style={{ color: C.faint }}>· optional, or describe if no upload</span>
            )}
          </div>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={
              mode === 'Image-first'
                ? 'Describe the image you have — subject, light, mood, color…'
                : 'What is the ad about? The angle, the offer, the audience, anything to anchor the copy.'
            }
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: C.surface2,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: '14px 15px',
              fontSize: 13,
              lineHeight: 1.6,
              color: C.secondary,
              minHeight: 96,
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />

          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1.4 }}>
              <div className="label-mono" style={{ marginBottom: 7 }}>
                Offer
              </div>
              <input
                value={offer}
                onChange={(e) => setOffer(e.target.value)}
                placeholder="Monsoon-season rate"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div className="label-mono" style={{ marginBottom: 7 }}>
                Code
              </div>
              <input
                value={offerCode}
                onChange={(e) => setOfferCode(e.target.value)}
                placeholder="PHUKET30"
                style={inputStyle}
              />
            </div>
          </div>

          {(() => {
            // A brief is written context, or (image-first) a readable image. A reel
            // isn't readable, so it always needs written notes to write from.
            const hasBrief =
              context.trim().length > 0 ||
              (mode === 'Image-first' && !!media && media.kind !== 'video');
            const disabled = status === 'loading' || !connected || !hasBrief;
            return (
              <>
                <Button
                  variant="primary"
                  onClick={() => !disabled && void generate()}
                  style={{
                    width: '100%',
                    marginTop: 22,
                    padding: 13,
                    fontSize: 14,
                    opacity: disabled ? 0.55 : 1,
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  {status === 'loading' ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 9,
                      }}
                    >
                      <Spinner size={15} /> Writing…
                    </span>
                  ) : (
                    '✦ Generate copy'
                  )}
                </Button>
                <div
                  style={{
                    textAlign: 'center',
                    marginTop: 11,
                    font: `500 10px 'IBM Plex Mono',monospace`,
                    color: C.faint,
                  }}
                >
                  {result
                    ? `${result.model} · ${result.variants.length} VARIANTS`
                    : !hasBrief
                      ? mode === 'Image-first'
                        ? 'UPLOAD AN IMAGE OR DESCRIBE ONE TO START'
                        : 'WRITE A BRIEF TO START'
                      : `2–3 VARIANTS · ${mode.toUpperCase()}`}
                </div>
              </>
            );
          })()}
        </Card>

        {/* results — top-aligned with the config card */}
        <div>
          {result && result.warnings.length > 0 && (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 14px',
                borderRadius: 10,
                background: alpha(C.watch, 9),
                border: `1px solid ${alpha(C.watch, 25)}`,
                font: `500 11.5px 'IBM Plex Mono',monospace`,
                color: C.secondary,
                lineHeight: 1.5,
              }}
            >
              {result.warnings.map((w, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <Icon name="alert" size={13} style={{ marginTop: 1 }} />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {status === 'idle' && !result && (
            <EmptyState title="Nothing generated yet">
              Pick a {axisLabel} and press generate. Basil writes on-brand, in the tenant&rsquo;s
              locked voice.
            </EmptyState>
          )}

          {status === 'loading' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SkeletonCard />
              <SkeletonCard dim />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  color: C.basil,
                  padding: 6,
                }}
              >
                <Spinner size={16} />
                <span
                  style={{
                    font: `500 11px 'IBM Plex Mono',monospace`,
                    letterSpacing: '.08em',
                    animation: 'basilpulse 1.4s ease-in-out infinite',
                  }}
                >
                  WRITING VARIANTS…
                </span>
              </div>
            </div>
          )}

          {status === 'done' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {variants.map((orig, i) => {
                const v = edits[i] ?? orig;
                return (
                  <Card key={i} style={{ padding: '22px 24px', animation: 'basilfade .35s ease' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 16,
                      }}
                    >
                      <span
                        className="pill"
                        style={{ color: selColor, background: alpha(selColor, 13) }}
                      >
                        {selGlyph} {v.hook_pattern}
                      </span>
                      <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.muted }}>
                        {v.char_count}/2200
                      </span>
                    </div>
                    <AutoTextarea
                      value={v.primary_text}
                      onChange={(val) => setField(i, 'primary_text', val)}
                      minRows={2}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        font: `400 18px/1.55 'Outfit',sans-serif`,
                        color: C.primary,
                        letterSpacing: '-.01em',
                        marginBottom: 16,
                        background: 'transparent',
                        border: `1px solid ${C.lineSoft}`,
                        borderRadius: 8,
                        padding: 6,
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        gap: 20,
                        paddingTop: 14,
                        borderTop: `1px solid ${C.lineSoft}`,
                        marginBottom: 14,
                      }}
                    >
                      <EditField
                        label="HEADLINE"
                        value={v.headline}
                        onChange={(val) => setField(i, 'headline', val)}
                        strong
                      />
                      <EditField
                        label="DESCRIPTION"
                        value={v.description}
                        onChange={(val) => setField(i, 'description', val)}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
                      <Field label="BLOG ANGLE" value={v.blog_angle} />
                      <Field
                        label={mode === 'Image-first' ? 'IMAGE NOTE' : 'SHOT BRIEF'}
                        value={v.shot_brief}
                      />
                    </div>
                    {v.warnings.length > 0 && (
                      <div
                        style={{
                          marginBottom: 14,
                          font: `500 10.5px 'IBM Plex Mono',monospace`,
                          color: C.watch,
                        }}
                      >
                        {v.warnings.map((w, j) => (
                          <div key={j} style={{ display: 'flex', gap: 5 }}>
                            <Icon name="alert" size={11} style={{ marginTop: 1 }} />
                            <span>{w}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 9 }}>
                      <Button
                        variant="primary"
                        onClick={() => void save(v, i)}
                        style={{
                          padding: '8px 15px',
                          fontSize: 12,
                          opacity: saved.has(i) ? 0.6 : 1,
                        }}
                      >
                        {saved.has(i) ? '✓ Saved' : 'Save post'}
                      </Button>
                      <Button
                        onClick={() => void navigator.clipboard?.writeText(v.primary_text)}
                        style={{ padding: '8px 14px', fontSize: 12 }}
                      >
                        Copy
                      </Button>
                      <Button
                        onClick={() => void generate()}
                        style={{ padding: '8px 14px', fontSize: 12 }}
                      >
                        ↻ Regenerate
                      </Button>
                    </div>
                    {saved.has(i) && savedIds[i] && (
                      <div style={{ marginTop: 10, fontSize: 12, color: C.good }}>
                        ✓ Saved to Posts.{' '}
                        {/* Styling only applies to a static image — a reel can't be styled. */}
                        {media?.kind !== 'video' && (
                          <>
                            <span
                              onClick={() => navigate(`/studio/create?creative=${savedIds[i]}`)}
                              style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                            >
                              ✦ Style the image →
                            </span>
                            <span style={{ color: C.faint }}> · </span>
                          </>
                        )}
                        <span
                          onClick={() => navigate(`/launch?mode=new&creative=${savedIds[i]}`)}
                          style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                        >
                          ⇢ Send to Launch →
                        </span>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: C.surface2,
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 12px',
  fontSize: 13,
  color: C.primary,
  fontFamily: 'inherit',
};

function EditField({
  label,
  value,
  onChange,
  strong,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  strong?: boolean;
}) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{
          font: `500 9px 'IBM Plex Mono',monospace`,
          letterSpacing: '.12em',
          color: C.faint,
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <AutoTextarea
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          fontSize: strong ? 14 : 13,
          lineHeight: 1.45,
          fontWeight: strong ? 600 : 400,
          color: strong ? C.primary : C.secondary,
          background: 'transparent',
          border: `1px solid ${C.lineSoft}`,
          borderRadius: 6,
          padding: '3px 5px',
          fontFamily: 'inherit',
        }}
      />
    </div>
  );
}

function Field({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{
          font: `500 9px 'IBM Plex Mono',monospace`,
          letterSpacing: '.12em',
          color: C.faint,
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: strong ? 14 : 13,
          fontWeight: strong ? 600 : 400,
          color: strong ? C.primary : C.secondary,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/** A textarea that grows to fit its content — never clips, never micro-scrolls. */
function AutoTextarea({
  value,
  onChange,
  style,
  minRows = 1,
}: {
  value: string;
  onChange: (v: string) => void;
  style?: React.CSSProperties;
  minRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={minRows}
      style={{ ...style, resize: 'none', overflow: 'hidden' }}
    />
  );
}

/** Spinning ring; inherits the surrounding text color via currentColor. */
function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        opacity: 0.9,
        animation: 'basilspin .7s linear infinite',
      }}
    />
  );
}

function SkeletonCard({ dim }: { dim?: boolean }) {
  const bar = (w: string, h = 20, mb = 10) => (
    <div
      style={{
        height: h,
        width: w,
        borderRadius: 5,
        marginBottom: mb,
        background: `linear-gradient(90deg,${C.surface2} 25%,${C.elevated} 37%,${C.surface2} 63%)`,
        backgroundSize: '840px 100%',
        animation: 'basilshimmer 1.4s infinite linear',
      }}
    />
  );
  return (
    <Card style={{ padding: 22, opacity: dim ? 0.7 : 1 }}>
      {bar('120px', 14, 18)}
      {bar('92%')}
      {bar('72%', 20, 0)}
    </Card>
  );
}
